import { patientIdSchema, visitIdSchema } from "@repo/api/schemas";
import {
  replaceOrderPlannedTrials,
  setOrderParameter,
  useOrderSet,
  usePatient,
  usePerformanceMeasureScores,
  useVisit,
  type VisitDto,
} from "@repo/data-store";
import type { PatientId, VisitId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { AddPatientExam } from "@/app/surfaces/add-patient-exam";
import { PatientSidePanel } from "@/app/surfaces/patient-history/patient-side-panel";
import { ConfigureProtocolPanel } from "@/features/cases";
import { useOrderSetDraft } from "@/features/cases/hooks/use-order-set-draft";
import { useProcedureOrderOptions } from "@/features/cases/hooks/use-procedure-order-options";
import {
  OrderSetPanel,
  useDataStoreVisitDetailController,
  VisitCharting,
  VisitDetail,
  VisitNoteSection,
  VisitStageBanner,
} from "@/features/visits";
import { ExamsPanel } from "@/features/visits/components/exams-panel";
import { VisitChartingOutline } from "@/features/visits/components/visit-charting-outline";
import { VisitFindings } from "@/features/visits/components/visit-findings";
import { VisitRail } from "@/features/visits/components/visit-rail";
import { useVisitAssessments } from "@/features/visits/hooks/use-visit-assessments";
import { useVisitFindings } from "@/features/visits/hooks/use-visit-findings";
import type { PresentedOrderSetDto } from "@/lib/order-set-presentation";
import { usePresentedOrderSet } from "@/lib/use-order-set-presentation";

export const Route = createFileRoute("/patients/$patientId/_layout/visits/$visitId")({
  component: RouteComponent,
});

function RouteComponent() {
  const visitId = visitIdSchema.parse(Route.useParams().visitId);
  const patientId = patientIdSchema.parse(Route.useParams().patientId);
  return <VisitScreen key={visitId} patientId={patientId} visitId={visitId} />;
}

function VisitScreen({ patientId, visitId }: { patientId: PatientId; visitId: VisitId }) {
  const { visit, isLoading } = useVisit(visitId);

  const presentation = usePresentedOrderSet(useOrderSet({ visitId }));
  const assessments = useVisitAssessments(patientId, visitId);
  const performanceMeasureScores = usePerformanceMeasureScores(patientId);
  const looseFindings = useVisitFindings(patientId, visitId);
  const orderSet = presentation.status === "ready" ? presentation.data : undefined;
  const actions = orderSet?.orders ?? [];
  const diagnosticProcedures = orderSet?.diagnosticProcedures ?? [];
  usePatient(patientId);
  const scheduleId = visit?.scheduleId ?? null;

  const encounterId = visit?.encounterId;
  const procedureOptions = useProcedureOrderOptions();

  if (isLoading || presentation.status === "loading") {
    return <div className="p-4">Loading visit...</div>;
  }

  if (presentation.status === "missing") {
    return <div className="p-4 text-destructive">Pinned protocol catalog metadata is unavailable.</div>;
  }

  if (!visit) {
    return <div className="p-4">Visit not found</div>;
  }

  const visitStatus = visit.status;

  const shownFindings = looseFindings.findings.filter(({ id }) => looseFindings.isShown(id));
  const visitDetailCard = <VisitDetailSidebar visit={visit} orderSet={orderSet} scheduleId={scheduleId} />;
  const assessmentsPanel = (
    <ExamsPanel
      performanceMeasures={assessments.measures}
      exams={assessments.exams}
      findings={looseFindings.findings}
      findingVisibility={looseFindings}
      onFindingNavigate={looseFindings.focus}
      action={
        <AddPatientExam
          patientId={patientId}
          visitId={visitId}
          onSelectFinding={looseFindings.add}
          trigger={(props) => (
            <Button variant="ghost" size="icon-xs" aria-label="Add assessment" {...props}>
              <Plus className="size-4" />
            </Button>
          )}
        />
      }
    />
  );

  return (
    <div>
      <VisitStageBanner
        status={visitStatus}
        encounterStatus={visit.encounterStatus}
        encounterClass={visit.encounterClass}
      />

      <div className="p-4 grid gap-6 grid-cols-1 lg:pb-0 lg:grid-cols-[300px_1fr] xl:grid-cols-[300px_1fr_auto]">
        <VisitRail>
          {visitDetailCard}
          {assessmentsPanel}
          {orderSet && (
            <OrderSetPanel
              orderSet={orderSet}
              scheduleConfig={
                <VisitProtocolConfiguration
                  orderSet={orderSet}
                  timingEditable={false}
                  bookedVisitId={visitId}
                  showOrderCards={false}
                  procedureOptions={procedureOptions}
                />
              }
              onSetParameter={(input) => setOrderParameter(input)}
              onReplacePlannedTrials={(input) => replaceOrderPlannedTrials(input)}
            />
          )}
        </VisitRail>

        <div className="space-y-4 lg:pb-4">
          <div className="space-y-4 lg:hidden">
            {visitDetailCard}
            {assessmentsPanel}
          </div>

          <VisitNoteSection key={visit.visitId} visitId={visit.visitId} start={visit.start} patientId={patientId} />

          <VisitFindings
            patientId={patientId}
            visitId={visitId}
            focusRequest={looseFindings.focusRequest}
            findings={looseFindings.findings}
            isShown={looseFindings.isShown}
            onEditStart={looseFindings.startEditing}
            onEditEnd={looseFindings.stopEditing}
          />
          <VisitCharting
            encounterId={encounterId}
            patientId={visit.patientId ?? ""}
            visitId={visit.visitId}
            encounterStatus={visit.encounterStatus}
            actions={actions}
            exams={assessments.shownExams}
            performanceMeasures={assessments.shownMeasures}
            performanceMeasureScores={performanceMeasureScores}
            diagnosticProcedures={diagnosticProcedures}
          />
        </div>

        {/* Third column only where it fits. */}
        <PatientSidePanel
          patientId={patientId}
          visitId={visitId}
          rememberOpen={false}
          outline={
            <VisitChartingOutline
              findings={shownFindings}
              encounterId={encounterId}
              exams={assessments.shownExams}
              performanceMeasures={assessments.shownMeasures}
              actions={actions}
              diagnosticProcedures={diagnosticProcedures}
            />
          }
          className="-my-4 -mr-4 hidden border-l xl:flex"
        />
      </div>
    </div>
  );
}

function VisitDetailSidebar({
  visit,
  orderSet,
  scheduleId,
}: {
  visit: VisitDto;
  orderSet: PresentedOrderSetDto | undefined;
  scheduleId: string | null;
}) {
  const { value, dialogs } = useDataStoreVisitDetailController({
    visit,
    orderSet,
    scheduleId,
  });
  return (
    <VisitDetail.Provider value={value}>
      {/* Bare, not carded: the encounter heads the rail and the cards
          below are the things with a border. */}
      <div className="space-y-2.5 px-0.5">
        <div className="flex items-center gap-2">
          <VisitDetail.StatusBadge />
          <div className="ml-auto flex items-center gap-0.5">
            <VisitDetail.JoinAction iconOnly />
            <VisitDetail.ActionsMenu />
          </div>
        </div>
        <VisitDetail.ProtocolHeadline />
        <VisitDetail.MetaLine />
        <VisitDetail.Elapsed />
        <VisitDetail.Reason />
      </div>
      {dialogs}
    </VisitDetail.Provider>
  );
}

function VisitProtocolConfiguration(props: Omit<React.ComponentProps<typeof ConfigureProtocolPanel>, "draft">) {
  const draft = useOrderSetDraft(props.orderSet);
  return <ConfigureProtocolPanel {...props} draft={draft} />;
}
